export type QuoteIn = {
    price: number;
    currency?: string | null;
    rawCurrency?: string | null;
    quoteUnit?: string | null;
    unitScale?: number | null;
    currencySymbol?: string | null;
    exchange?: string | null;
    referencePrice?: number | null;
    symbol: string;
};

export type QuoteOut = {
    symbol: string;
    rawPrice: number;
    rawCurrency: string | null;
    price: number;
    currency: string | null;
    unitScale: 1 | 0.01;
};

export type NormalizedMarketQuote = {
    symbol: string;
    providerId?: string;
    price: number;
    rawPrice?: number;
    currency: string;
    rawCurrency?: string | null;
    quoteUnit?: string | null;
    unitScale?: 1 | 0.01;
    source?: string;
    fetchedAt?: string;
};

export type NormalizeQuoteForInstrumentInput = {
    rawPrice: number;
    rawCurrency?: string | null;
    quoteUnit?: string | null;
    unitScale?: number | null;
    exchange?: string | null;
    symbol: string;
    nativeCurrency?: string | null;
    referenceNativePrice?: number | null;
};

const PENCE_CURRENCY_CODES = new Set(['GBX', 'GBXP', 'GBPENCE', 'GBPENNY', 'GBP(PENCE)', 'GBP(PENNY)', 'GBP PENCE', 'GBP PENNY']);
const MAJOR_CURRENCY_CODES = new Set(['GBP', 'USD', 'EUR']);
const normalizeValue = (value?: string | null) => (value ? value.trim().toUpperCase() : null);

export const canonicalMoneyCurrency = (value?: string | null, fallback = 'GBP') => {
    const normalized = normalizeValue(value) || fallback.toUpperCase();
    return PENCE_CURRENCY_CODES.has(normalized) || normalized === 'GBP' ? 'GBP' : normalized;
};

export const isUkExchange = (exchange?: string | null) => {
    const ex = normalizeValue(exchange) || '';
    return ex.includes('LSE') || ex.includes('XLON') || ex.includes('LON') || ex.includes('LONDON');
};

const isExplicitPenceToken = (value?: string | null) => {
    const raw = value ? value.trim() : '';
    const normalized = normalizeValue(raw);
    if (!normalized) return false;
    if (raw === 'GBp' || raw === 'GBp.') return true;
    return PENCE_CURRENCY_CODES.has(normalized);
};

const isAround = (value: number, low: number, high: number) => Number.isFinite(value) && value >= low && value <= high;

const shouldScalePenceQuote = (opts: {
    rawPrice: number;
    rawCurrency?: string | null;
    quoteUnit?: string | null;
    unitScale?: number | null;
    nativeCurrency?: string | null;
    referenceNativePrice?: number | null;
}) => {
    const rawCurrency = normalizeValue(opts.rawCurrency);
    const nativeCurrency = canonicalMoneyCurrency(opts.nativeCurrency, rawCurrency || 'GBP');
    const configuredPenceScale = Number(opts.unitScale) === 0.01;
    const explicitPence = isExplicitPenceToken(opts.rawCurrency) || isExplicitPenceToken(opts.quoteUnit) || configuredPenceScale;

    if (!configuredPenceScale && rawCurrency && MAJOR_CURRENCY_CODES.has(rawCurrency) && !isExplicitPenceToken(opts.rawCurrency)) return false;
    if (!configuredPenceScale && (rawCurrency === 'USD' || nativeCurrency === 'USD')) return false;
    if (!explicitPence) return false;

    const rawPrice = Number(opts.rawPrice);
    const referenceNativePrice = Number(opts.referenceNativePrice);

    // LSE mixes pence-quoted shares with major-currency ETFs/ETCs. When a raw
    // provider value is already close to the native reference price, keep it in
    // major units even if stale metadata says GBX.
    if (isAround(rawPrice, 80, 160) && isAround(referenceNativePrice, 80, 160)) return false;

    // A classic UK share pence quote has a raw value in pence while imported
    // transactions are already native GBP major units around a few pounds.
    if (isAround(referenceNativePrice, 2, 5)) return true;

    // EODHD has returned SGLN-like GBX raw values around 6705 with native
    // references around GBP 67. Those must be scaled once before storage.
    if (rawPrice / 100 > 0 && isAround(rawPrice / 100, referenceNativePrice * 0.5, referenceNativePrice * 1.5)) return true;

    return true;
};

export const isPenceSterling = (opts: {
    currency?: string | null;
    rawCurrency?: string | null;
    quoteUnit?: string | null;
    unitScale?: number | null;
    currencySymbol?: string | null;
    exchange?: string | null;
    price?: number;
    referencePrice?: number | null;
}) => {
    const rawCurrency = normalizeValue(opts.rawCurrency);
    const currency = normalizeValue(opts.currency);
    if (rawCurrency && MAJOR_CURRENCY_CODES.has(rawCurrency) && !isExplicitPenceToken(opts.rawCurrency)) return false;
    if (isExplicitPenceToken(opts.rawCurrency) || isExplicitPenceToken(opts.quoteUnit) || isExplicitPenceToken(opts.currency)) {
        return shouldScalePenceQuote({
            rawPrice: Number(opts.price),
            rawCurrency: opts.rawCurrency || opts.currency,
            quoteUnit: opts.quoteUnit,
            unitScale: opts.unitScale,
            nativeCurrency: opts.currency,
            referenceNativePrice: opts.referencePrice,
        });
    }
    if (currency && MAJOR_CURRENCY_CODES.has(currency)) return false;

    const currencySymbol = opts.currencySymbol?.trim();
    if (currencySymbol === 'p' || currencySymbol === 'P') return true;

    return false;
};

export const normalizeMoneyUnit = (opts: {
    value: number;
    rawCurrency?: string | null;
    exchange?: string | null;
    symbol?: string | null;
    providerId?: string | null;
}) => {
    const rawCurrency = opts.rawCurrency ? opts.rawCurrency.trim() : null;
    const isPence = isPenceSterling({
        currency: rawCurrency,
        rawCurrency,
        exchange: opts.exchange,
        price: opts.value,
    });

    return {
        value: isPence ? Number((opts.value * 0.01).toFixed(8)) : opts.value,
        currency: isPence ? 'GBP' : canonicalMoneyCurrency(rawCurrency, 'GBP'),
        rawCurrency,
        unitScale: (isPence ? 0.01 : 1) as 1 | 0.01,
    };
};

// Raw provider response normalizer only. Do not call this for Firestore quote
// documents: Firestore quote.price is already normalized and display-ready.
export const normalizeQuoteForInstrument = (opts: NormalizeQuoteForInstrumentInput): QuoteOut => {
    const rawPrice = Number(opts.rawPrice);
    const rawCurrency = opts.rawCurrency ? opts.rawCurrency.trim() : null;
    const quoteUnit = opts.quoteUnit ? opts.quoteUnit.trim() : null;
    const rawCurrencyNormalized = normalizeValue(rawCurrency);
    const nativeCurrency = canonicalMoneyCurrency(opts.nativeCurrency || rawCurrency || 'GBP');
    const isPence = shouldScalePenceQuote({
        rawPrice,
        rawCurrency,
        quoteUnit,
        unitScale: opts.unitScale,
        nativeCurrency,
        referenceNativePrice: opts.referenceNativePrice,
    });
    const unitScale: 1 | 0.01 = isPence ? 0.01 : 1;
    const normalizedCurrency = isPence || isExplicitPenceToken(rawCurrency) || isExplicitPenceToken(quoteUnit)
        ? 'GBP'
        : rawCurrencyNormalized || nativeCurrency;

    return {
        symbol: opts.symbol,
        rawPrice,
        rawCurrency,
        price: Number((rawPrice * unitScale).toFixed(6)),
        currency: normalizedCurrency,
        unitScale,
    };
};

// Raw provider response normalizer only. Firestore/backend quote.price is final.
export const normaliseQuote = (q: QuoteIn): QuoteOut => {
    const tradingCurrency = q.currency ? q.currency.trim() : null;
    const rawCurrencyInput = q.rawCurrency ? q.rawCurrency.trim() : null;

    return normalizeQuoteForInstrument({
        symbol: q.symbol,
        rawPrice: q.price,
        rawCurrency: rawCurrencyInput,
        quoteUnit: q.quoteUnit,
        unitScale: q.unitScale,
        exchange: q.exchange,
        nativeCurrency: tradingCurrency || rawCurrencyInput,
        referenceNativePrice: q.referencePrice,
    });
};

export const isBackendNormalizedQuote = (quote: { price?: unknown; providerId?: unknown; fetchedAt?: unknown; source?: unknown } | null | undefined) =>
    Number.isFinite(Number(quote?.price)) && Boolean(quote?.providerId || quote?.fetchedAt || quote?.source);

export const getDisplayQuotePrice = (quote: { price?: unknown } | null | undefined): number | null => {
    const price = Number(quote?.price);
    return Number.isFinite(price) ? price : null;
};
